import { type PropsWithChildren, useState } from 'react';
import { Drawer, type DrawerProps, Modal, type ModalProps } from '@mantine/core';
import { type Breakpoint } from '../../types/breakpoint';
import { useBreakpoint } from '../../lib/hooks/useBreakpoint';

type CommonResponsiveModalProps = 'children' | 'opened' | 'onClose' | 'title';

const defaultCloseButtonProps = {
    'aria-label': 'Закрыть',
} as const;

export type ResponsiveModalProps = PropsWithChildren<{
    drawerProps?: Omit<DrawerProps, CommonResponsiveModalProps>;
    breakpoint?: Breakpoint;
    modalProps?: Omit<ModalProps, CommonResponsiveModalProps>;
    onClose: ModalProps['onClose'];
    opened: boolean;
    stackIndex?: number;
    title?: ModalProps['title'];
    topmost?: boolean;
}>;

const defaultDrawerProps = {
    size: 'lg',
    position: 'bottom',
    withCloseButton: true,
    radius: 'lg',
    closeButtonProps: defaultCloseButtonProps,
} satisfies NonNullable<ResponsiveModalProps['drawerProps']>;

const defaultModalProps = {
    centered: true,
    closeButtonProps: defaultCloseButtonProps,
} satisfies NonNullable<ResponsiveModalProps['modalProps']>;

const baseZIndex = 220;

export const ResponsiveModal = ({
    children,
    breakpoint = 'mobile',
    drawerProps,
    modalProps,
    opened,
    stackIndex = 0,
    title,
    onClose,
    topmost = true,
}: ResponsiveModalProps) => {
    const isMobile = useBreakpoint(breakpoint);

    const [presentationIsMobile, setPresentationIsMobile] = useState(isMobile);
    const [previousOpened, setPreviousOpened] = useState(opened);

    if (opened !== previousOpened) {
        setPreviousOpened(opened);

        if (opened) {
            setPresentationIsMobile(isMobile);
        }
    }

    const inactiveDrawerProps: NonNullable<ResponsiveModalProps['drawerProps']> = topmost
        ? {}
        : {
              'aria-hidden': true,
              inert: true,
              closeOnClickOutside: false,
              closeOnEscape: false,
              lockScroll: false,
              returnFocus: false,
              trapFocus: false,
              withOverlay: false,
              style: [drawerProps?.style, { pointerEvents: 'none' as const }],
          };

    const inactiveModalProps: NonNullable<ResponsiveModalProps['modalProps']> = topmost
        ? {}
        : {
              'aria-hidden': true,
              inert: true,
              closeOnClickOutside: false,
              closeOnEscape: false,
              lockScroll: false,
              returnFocus: false,
              trapFocus: false,
              withOverlay: false,
              style: [modalProps?.style, { pointerEvents: 'none' as const }],
          };

    const resolvedDrawerProps = {
        ...defaultDrawerProps,
        ...drawerProps,
        ...inactiveDrawerProps,
        closeButtonProps: {
            ...defaultCloseButtonProps,
            ...drawerProps?.closeButtonProps,
        },
        zIndex: baseZIndex + stackIndex,
    };

    const resolvedModalProps = {
        ...defaultModalProps,
        ...modalProps,
        ...inactiveModalProps,
        closeButtonProps: {
            ...defaultCloseButtonProps,
            ...modalProps?.closeButtonProps,
        },
        zIndex: baseZIndex + stackIndex,
    };

    if (presentationIsMobile) {
        return (
            <Drawer
                {...resolvedDrawerProps}
                opened={opened}
                title={title}
                onClose={onClose}
            >
                {children}
            </Drawer>
        );
    }

    return (
        <Modal
            {...resolvedModalProps}
            opened={opened}
            title={title}
            onClose={onClose}
        >
            {children}
        </Modal>
    );
};
